'use client';

import { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useSSOStore } from '@/lib/sso/store';
import { GroupMappingTable } from './group-mapping-table';
import {
  Users,
  RefreshCw,
  Copy,
  Check,
  Eye,
  EyeOff,
  AlertTriangle,
  CheckCircle2,
  Clock,
} from 'lucide-react';

export function SCIMConfigPage() {
  const {
    scimConfig,
    scimLoading,
    scimError,
    newScimToken,
    scimGroups,
    loadSCIMConfig,
    updateSCIMConfig,
    generateSCIMToken,
    rotateSCIMToken,
    triggerSync,
    clearNewToken,
    loadSCIMGroups,
    clearErrors,
  } = useSSOStore();

  const [showToken, setShowToken] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    loadSCIMConfig();
    loadSCIMGroups();
    return () => clearErrors();
  }, [loadSCIMConfig, loadSCIMGroups, clearErrors]);

  const handleCopyToken = () => {
    if (newScimToken) {
      navigator.clipboard.writeText(newScimToken);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleGenerateToken = async () => {
    try {
      await generateSCIMToken();
    } catch (error) {
      // Error handled by store
    }
  };

  const handleRotateToken = async () => {
    if (confirm('Are you sure you want to rotate the SCIM token? The old token will stop working immediately.')) {
      try {
        await rotateSCIMToken();
      } catch (error) {
        // Error handled by store
      }
    }
  };

  const handleSync = async () => {
    try {
      await triggerSync();
    } catch (error) {
      // Error handled by store
    }
  };

  if (scimLoading && !scimConfig) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-[300px] w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Users className="size-6" />
            SCIM Directory Sync
          </h1>
          <p className="text-muted-foreground mt-1">
            Automatically sync users and groups from your identity provider
          </p>
        </div>
        <Badge variant={scimConfig?.is_enabled ? 'default' : 'secondary'}>
          {scimConfig?.is_enabled ? 'Enabled' : 'Disabled'}
        </Badge>
      </div>

      {scimError && (
        <Alert variant="destructive">
          <AlertTriangle className="size-4" />
          <AlertDescription>{scimError}</AlertDescription>
        </Alert>
      )}

      {/* SCIM Configuration */}
      <Card>
        <CardHeader>
          <CardTitle>Configuration</CardTitle>
          <CardDescription>
            Configure SCIM 2.0 provisioning settings
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Enable SCIM */}
          <div className="flex items-center justify-between">
            <div>
              <Label className="text-base">Enable SCIM</Label>
              <p className="text-sm text-muted-foreground">
                Allow your identity provider to manage users and groups
              </p>
            </div>
            <Switch
              checked={scimConfig?.is_enabled ?? false}
              onCheckedChange={(checked) => updateSCIMConfig({ is_enabled: checked })}
              disabled={scimLoading}
            />
          </div>

          {/* SCIM Endpoint */}
          <div>
            <Label>SCIM Endpoint URL</Label>
            <Input
              value={`${window.location.origin}/api/v1/scim/v2`}
              readOnly
              className="mt-1 bg-muted"
            />
            <p className="text-sm text-muted-foreground mt-1">
              Configure this URL in your identity provider's SCIM settings
            </p>
          </div>

          {/* SCIM Token */}
          <div className="space-y-3">
            <Label>SCIM Token</Label>

            {newScimToken ? (
              <Alert>
                <AlertTriangle className="size-4" />
                <AlertDescription className="space-y-3">
                  <p className="font-medium">Save this token now - it won't be shown again!</p>
                  <div className="flex items-center gap-2">
                    <Input
                      value={showToken ? newScimToken : '••••••••••••••••••••••••••••••••'}
                      readOnly
                      className="font-mono text-sm bg-background"
                    />
                    <Button variant="ghost" size="icon" onClick={() => setShowToken(!showToken)}>
                      {showToken ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
                    </Button>
                    <Button variant="ghost" size="icon" onClick={handleCopyToken}>
                      {copied ? <Check className="size-4" /> : <Copy className="size-4" />}
                    </Button>
                  </div>
                  <Button variant="outline" size="sm" onClick={clearNewToken}>
                    I've saved the token
                  </Button>
                </AlertDescription>
              </Alert>
            ) : scimConfig?.scim_token_prefix ? (
              <div className="flex items-center gap-3">
                <div>
                  <p className="font-mono text-sm">{scimConfig.scim_token_prefix}••••••••</p>
                  <p className="text-xs text-muted-foreground">Token prefix shown for identification</p>
                </div>
                <Button variant="outline" size="sm" onClick={handleRotateToken} disabled={scimLoading}>
                  <RefreshCw className="size-4 mr-2" />
                  Rotate Token
                </Button>
              </div>
            ) : (
              <Button onClick={handleGenerateToken} disabled={scimLoading}>
                Generate SCIM Token
              </Button>
            )}
          </div>

          {/* Sync Settings */}
          <div className="space-y-4 pt-4 border-t">
            <h4 className="font-medium">Sync Settings</h4>

            <div className="flex items-center justify-between">
              <div>
                <Label>Sync Users</Label>
                <p className="text-sm text-muted-foreground">
                  Provision and deprovision users from IdP
                </p>
              </div>
              <Switch
                checked={scimConfig?.sync_users ?? true}
                onCheckedChange={(checked) => updateSCIMConfig({ sync_users: checked })}
                disabled={scimLoading || !scimConfig?.is_enabled}
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <Label>Sync Groups</Label>
                <p className="text-sm text-muted-foreground">
                  Sync group memberships and map to roles
                </p>
              </div>
              <Switch
                checked={scimConfig?.sync_groups ?? true}
                onCheckedChange={(checked) => updateSCIMConfig({ sync_groups: checked })}
                disabled={scimLoading || !scimConfig?.is_enabled}
              />
            </div>

            <div>
              <Label>Deprovision Action</Label>
              <Select
                value={scimConfig?.deprovision_action ?? 'suspend'}
                onValueChange={(value) => updateSCIMConfig({
                  deprovision_action: value as 'suspend' | 'delete' | 'disable',
                })}
                disabled={scimLoading || !scimConfig?.is_enabled}
              >
                <SelectTrigger className="mt-1 w-[200px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="suspend">Suspend Account</SelectItem>
                  <SelectItem value="disable">Disable Login</SelectItem>
                  <SelectItem value="delete">Delete Account</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-sm text-muted-foreground mt-1">
                Action to take when a user is removed from the IdP
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Sync Status */}
      {scimConfig?.is_enabled && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Sync Status</CardTitle>
                <CardDescription>
                  View sync history and trigger manual sync
                </CardDescription>
              </div>
              <Button
                variant="outline"
                onClick={handleSync}
                disabled={scimLoading}
              >
                <RefreshCw className={`size-4 mr-2 ${scimLoading ? 'animate-spin' : ''}`} />
                Sync Now
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <p className="text-sm text-muted-foreground">Last Sync</p>
                <p className="font-medium flex items-center gap-1.5">
                  <Clock className="size-4" />
                  {scimConfig.last_sync_at
                    ? new Date(scimConfig.last_sync_at).toLocaleString()
                    : 'Never'}
                </p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Status</p>
                <p className="font-medium flex items-center gap-1.5">
                  {scimConfig.last_sync_status === 'success' ? (
                    <><CheckCircle2 className="size-4 text-green-500" /> Success</>
                  ) : scimConfig.last_sync_status === 'error' ? (
                    <><AlertTriangle className="size-4 text-red-500" /> Error</>
                  ) : (
                    <><Clock className="size-4 text-muted-foreground" /> Pending</>
                  )}
                </p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Users Synced</p>
                <p className="font-medium">{scimConfig.total_users_synced}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Groups Synced</p>
                <p className="font-medium">{scimConfig.total_groups_synced}</p>
              </div>
            </div>

            {scimConfig.last_sync_error && (
              <Alert variant="destructive" className="mt-4">
                <AlertTriangle className="size-4" />
                <AlertDescription>{scimConfig.last_sync_error}</AlertDescription>
              </Alert>
            )}
          </CardContent>
        </Card>
      )}

      {/* Group Mapping */}
      {scimConfig?.is_enabled && scimConfig.sync_groups && (
        <GroupMappingTable groups={scimGroups} />
      )}
    </div>
  );
}
